import { UsedOutsideProviderError } from '@diritto/common/utils/exceptions';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { FancySharePublicDto, FancySharesResponseDto } from '@diritto/common/fancyShare/dtos/fancy-share.dtos';
import { flatItems } from '@diritto/common/utils/array';
import { ProfileRelayDto } from '@diritto/common/relay/dtos/profile-relay.dtos';
import { FancyShareDispatchState, useFancyShareDispatch } from 'state-hooks/fancyShare/useFancyShareDispatch';
import { ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodeRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { useFancyShareExtra } from 'state-hooks/fancyShare/useFancyShareExtra';

interface FancyShareState {
  isPending: boolean;
  fancyShares: FancySharePublicDto[];
  hasNextPage: boolean;
  fetchNextPage: () => Promise<any>;
  profiles: Record<string, ProfileRelayDto>;
  contents: Record<string, ContentRelayDto>;
  episodes: Record<string, EpisodeRelayDto>;
  likeFancyShareIds: number[];
}

const FancyShareStateContext = createContext<FancyShareState>({
  isPending: true,
  fancyShares: [],
  hasNextPage: false,
  fetchNextPage: () => Promise.resolve(undefined),
  profiles: {},
  contents: {},
  episodes: {},
  likeFancyShareIds: [],
});

const FancyShareDispatchContext = createContext<FancyShareDispatchState>({
  dispatchAction(): Promise<void> {
    return Promise.resolve(undefined);
  },
  fancyShare: undefined,
  isPending: false,
  isInputFocus: false,
});

export const FancyShareProvider = ({
  children,
  fancySharesQuery,
}: {
  children: ReactNode;
  fancySharesQuery: UseInfiniteQueryResult<InfiniteData<FancySharesResponseDto>, unknown>;
}) => {
  const { fancyShares } = useMemo(
    () => ({
      fancyShares: flatItems<FancySharePublicDto>('fancyShares', fancySharesQuery.data?.pages) ?? [],
    }),
    [fancySharesQuery.data?.pages],
  );

  const fancyShareExtraState = useFancyShareExtra(fancyShares);
  const state = useMemo<FancyShareState>(
    () => ({
      ...fancyShareExtraState,
      isExtraLoading: fancyShareExtraState.isPending,
      isPending: fancySharesQuery.isPending,
      hasNextPage: !!fancySharesQuery.hasNextPage,
      fetchNextPage: fancySharesQuery.fetchNextPage,
      fancyShares,
    }),
    [
      fancyShareExtraState,
      fancyShares,
      fancySharesQuery.fetchNextPage,
      fancySharesQuery.hasNextPage,
      fancySharesQuery.isPending,
    ],
  );

  return (
    <FancyShareStateContext.Provider value={state}>
      <FancyShareDispatchContext.Provider value={useFancyShareDispatch(fancySharesQuery.refetch)}>
        {children}
      </FancyShareDispatchContext.Provider>
    </FancyShareStateContext.Provider>
  );
};

export const useFancyShareState = () => {
  const context = useContext(FancyShareStateContext);

  if (context === undefined) {
    throw new UsedOutsideProviderError('useFancyShare State was used outside of its Provider');
  }
  return context;
};

export const useFancyShareDispatchState = () => {
  const context = useContext(FancyShareDispatchContext);

  if (context === undefined) {
    throw new UsedOutsideProviderError('useFancyShare State was used outside of its Provider');
  }
  return context;
};
